import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const monacoYachtShow: ChauffeurPage = {
  slug: "monaco-yacht-show",
  title: "Chauffeur privé Monaco Yacht Show – VTC UHNW",
  subtitle:
    "Chauffeur privé pendant le Monaco Yacht Show : transferts aéroport ↔ Port Hercule, runs propriétaires et équipages, service discret UHNW.",
  keywords: [
    "chauffeur monaco yacht show",
    "vtc monaco yacht show",
    "chauffeur privé monaco yacht show",
  ],
  hero: {
    title: "Chauffeur privé pendant le Monaco Yacht Show",
    subtitle:
      "Fin septembre, l'industrie du superyacht se réunit à Port Hercule : un chauffeur discret pour propriétaires, brokers et équipages.",
    keywords: [
      "chauffeur Monaco Yacht Show",
      "VTC Port Hercule",
      "chauffeur superyacht Monaco",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le Monaco Yacht Show",
    badges: ["Aéroport ↔ Port Hercule", "Runs équipages", "Discrétion UHNW"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant le Yacht Show",
    items: [
      {
        title: "Aéroport ↔ Port Hercule",
        description:
          "Liaison directe Nice ↔ Monaco pour propriétaires et invités arrivant à Port Hercule, avec suivi de vol.",
      },
      {
        title: "Clientèle UHNW",
        description:
          "Propriétaires, brokers et family offices : discrétion absolue, chauffeur en tenue et confidentialité totale.",
      },
      {
        title: "Runs équipages",
        description:
          "Allers-retours pour capitaines, équipages et prestataires entre quais, hôtels et fournisseurs de la Principauté.",
      },
      {
        title: "Accès quais malgré l'affluence",
        description:
          "Port Hercule fermé au public pendant le salon : dépose au plus près des passerelles et des stands selon les accès.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le Monaco Yacht Show",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Nice ↔ Monaco avec suivi de vol et coordination possible avec l'héliport de Monaco.",
      },
      {
        title: "Accès Port Hercule",
        text: "Dépose et reprise au plus près des passerelles, stands et tenders selon les accès du salon.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié pour enchaîner visites de yachts, rendez-vous brokers et déjeuners d'affaires.",
      },
      {
        title: "Runs propriétaires & équipages",
        text: "Trajets discrets pour propriétaires, invités, capitaines et équipages tout au long du salon.",
      },
      {
        title: "Soirées & dîners privés",
        text: "Réceptions à bord, dîners de prestige et soirées en Principauté, retour de nuit assuré.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le Yacht Show",
    items: [
      {
        name: "Port Hercule",
        description:
          "Quais d'exposition des superyachts, stands et passerelles.",
      },
      {
        name: "Monte-Carlo & Casino",
        description: "Palaces, réceptions et dîners de prestige.",
      },
      {
        name: "Héliport de Monaco (Fontvieille)",
        description:
          "Liaisons rapides avec Nice pour propriétaires et invités.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, arrivées concentrées fin septembre.",
      },
      {
        name: "La Condamine & Fontvieille",
        description: "Fournisseurs, hôtels et logistique des équipages.",
      },
    ],
  },
  generic: {
    heading: "Le Monaco Yacht Show vu d'un chauffeur",
    subheading:
      "Fin septembre, Port Hercule devient la vitrine mondiale du superyacht — un public d'owners, brokers et UHNW.",
    paragraphs: [
      "Chaque fin septembre, le Monaco Yacht Show réunit à Port Hercule les plus grands superyachts du monde, leurs propriétaires, brokers et family offices. Le port se ferme au public et les accès aux passerelles se font sur invitation : un chauffeur qui connaît la Principauté dépose au plus près des stands et des quais selon les accès du salon.",
      "Les arrivées se concentrent à l'aéroport de Nice Côte d'Azur. Suivi de vol, accueil discret et liaison directe vers Port Hercule, avec coordination possible par l'héliport de Fontvieille pour les emplois du temps les plus serrés.",
      "Le salon mêle visites de yachts, rendez-vous brokers, dîners à bord et runs d'équipages. La mise à disposition garde le même chauffeur pour propriétaires comme pour capitaines, avec la discrétion absolue qu'attend une clientèle UHNW et les retours de nuit après les réceptions.",
    ],
    bullets: [
      "Dépose au plus près des passerelles",
      "Suivi de vol fin septembre",
      "Discrétion UHNW",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le Monaco Yacht Show",
    steps: [
      "Indiquez vos dates, votre yacht ou hôtel et vos rendez-vous (stands, brokers, dîners).",
      "Nous calons les liaisons aéroport ↔ Port Hercule et les déposes selon les accès du salon.",
      "Le chauffeur se présente en tenue, avec suivi de vol et coordination héliport si besoin.",
      "Mise à disposition journée et soirée pour visites, rendez-vous et réceptions.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur pendant le Monaco Yacht Show ?",
      a: "Réservez en amont : fin septembre, la Principauté est très sollicitée. Transmettez vos dates, votre yacht ou hôtel et vos rendez-vous pour que nous bloquions un chauffeur dédié, avec la discrétion attendue d'une clientèle UHNW.",
    },
    {
      q: "Pouvez-vous déposer au plus près des quais de Port Hercule ?",
      a: "Oui, dans la limite des accès du salon. Pendant le Yacht Show, le port est fermé au public ; le chauffeur dépose au point autorisé le plus proche des passerelles et stands, puis vous reprend selon les fenêtres d'accès.",
    },
    {
      q: "Gérez-vous la liaison entre l'aéroport de Nice et Port Hercule ?",
      a: "Oui, avec suivi de vol et accueil discret. Nous assurons la liaison directe Nice ↔ Monaco et pouvons coordonner avec l'héliport de Monaco pour les arrivées de propriétaires et d'invités aux emplois du temps serrés.",
    },
    {
      q: "Assurez-vous aussi les trajets des équipages et prestataires ?",
      a: "Oui. Au-delà des propriétaires et invités, nous organisons les runs de capitaines, équipages et prestataires entre quais, hôtels et fournisseurs de la Principauté tout au long du salon.",
    },
  ],
  relatedLinks: [
    {
      label: "Monaco vers Aéroport de Nice avec chauffeur",
      href: "/chauffeur/transferts/monaco-aeroport-nice",
    },
    {
      label: "Monaco vers Saint-Tropez avec chauffeur",
      href: "/chauffeur/transferts/monaco-saint-tropez",
    },
    {
      label: "Réserver un trajet",
      href: "/reservation",
    },
  ],
  cta: {
    title: "Votre chauffeur pour le Monaco Yacht Show",
    text: "Aéroport ↔ Port Hercule, runs équipages et service discret : réservez un chauffeur dédié pour le salon.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title:
      "Chauffeur Monaco Yacht Show — VTC UHNW Port Hercule | KS Rent Services",
    description:
      "Chauffeur privé pendant le Monaco Yacht Show : transferts aéroport ↔ Port Hercule, runs propriétaires et équipages, héliport et service discret UHNW. Réservez.",
    canonical: "/chauffeur/monaco-yacht-show",
  },
};

export default monacoYachtShow;
